import React from 'react';
import { rankOf, suitOf } from '@big2/shared';
import { type CardBackId, useCardBack } from '../cardBacks.js';

const SUIT_SYMBOLS = { D: '♦', C: '♣', H: '♥', S: '♠' };
const RANK_LABELS: Record<string, string> = {
  '3': '3',
  '4': '4',
  '5': '5',
  '6': '6',
  '7': '7',
  '8': '8',
  '9': '9',
  T: '10',
  J: 'J',
  Q: 'Q',
  K: 'K',
  A: 'A',
  '2': '2',
};

export function formatCardDisplay(cardCode?: string): string {
  if (!cardCode || cardCode.length < 2) return '';
  const r = cardCode.slice(0, -1);
  const s = cardCode.slice(-1) as 'D' | 'C' | 'H' | 'S';
  const rankLabel = RANK_LABELS[r] ?? r;
  const suitSymbol = SUIT_SYMBOLS[s] ?? s;
  return `${rankLabel}${suitSymbol}`;
}

interface CardProps {
  cardCode?: string;
  selected?: boolean;
  isOpeningCard?: boolean;
  isPlayable?: boolean;
  isDimmed?: boolean;
  isTrump?: boolean;
  onClick?: () => void;
  onPointerDown?: (e: React.PointerEvent<HTMLDivElement>) => void;
  style?: React.CSSProperties;
  faceDown?: boolean;
  cardBackId?: CardBackId;
  className?: string;
}

export function renderCardBackDesign(backId: CardBackId) {
  switch (backId) {
    case 'soyombo':
      return (
        <div
          style={{
            width: '100%',
            height: '100%',
            borderRadius: 5,
            background: 'radial-gradient(circle at 50% 45%, #1d4ed8 0%, #0f172a 100%)',
            border: '2px solid #f59e0b',
            boxShadow: 'inset 0 0 0 2px #991b1b, inset 0 0 10px rgba(251, 191, 36, 0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Corner ornaments */}
          <div
            style={{
              position: 'absolute',
              inset: 4,
              border: '1px solid rgba(251, 191, 36, 0.55)',
              borderRadius: 3,
              pointerEvents: 'none',
            }}
          />
          {/* Traditional Soyombo-inspired Golden Emblem (Flame, Sun, Moon, Yin-Yang, Pillars) */}
          <svg width="36" height="58" viewBox="0 0 36 58" fill="none">
            {/* Three-tongue flame */}
            <path d="M18 3 C16 7, 15 9, 18 11 C21 9, 20 7, 18 3 Z" fill="#fbbf24" />
            <path d="M13 6 C12 8, 12 10, 14 11 C15 9, 14 8, 13 6 Z" fill="#f59e0b" />
            <path d="M23 6 C22 8, 21 9, 22 11 C24 10, 24 8, 23 6 Z" fill="#f59e0b" />
            {/* Sun & Crescent Moon */}
            <circle cx="18" cy="15" r="3" fill="#fde68a" />
            <path d="M12 17 A6 5 0 0 0 24 17 A6 3.5 0 0 1 12 17 Z" fill="#fbbf24" />
            {/* Top Triangle */}
            <polygon points="12,22 24,22 18,26" fill="#fbbf24" />
            {/* Top Bar */}
            <rect x="12" y="27" width="12" height="2.2" fill="#fde68a" />
            {/* Side Pillars */}
            <rect x="7.5" y="22" width="3" height="25" rx="1" fill="#f59e0b" />
            <rect x="25.5" y="22" width="3" height="25" rx="1" fill="#f59e0b" />
            {/* Center Yin-Yang / Two Fishes */}
            <circle cx="18" cy="34.5" r="4.8" stroke="#fbbf24" strokeWidth="1.4" fill="#1e3a8a" />
            <path d="M18 29.7 A2.4 2.4 0 0 1 18 34.5 A2.4 2.4 0 0 0 18 39.3 A4.8 4.8 0 0 0 18 29.7 Z" fill="#fbbf24" />
            <circle cx="18" cy="32.1" r="0.9" fill="#1e3a8a" />
            <circle cx="18" cy="36.9" r="0.9" fill="#fbbf24" />
            {/* Bottom Bar */}
            <rect x="12" y="40.5" width="12" height="2.2" fill="#fde68a" />
            {/* Bottom Triangle */}
            <polygon points="12,44 24,44 18,48" fill="#fbbf24" />
          </svg>
        </div>
      );

    case 'ulzii_emerald':
      return (
        <div
          style={{
            width: '100%',
            height: '100%',
            borderRadius: 5,
            background: 'radial-gradient(circle at 50% 50%, #059669 0%, #064e3b 70%, #022c22 100%)',
            border: '2px solid #fcd34d',
            boxShadow: 'inset 0 0 0 2px rgba(252, 211, 77, 0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 5,
              border: '1px dashed rgba(252, 211, 77, 0.5)',
              borderRadius: 3,
            }}
          />
          {/* Ulzii Endless Knot SVG */}
          <svg width="42" height="42" viewBox="0 0 42 42" fill="none">
            <rect x="8" y="8" width="26" height="26" rx="3" stroke="#fcd34d" strokeWidth="1.8" />
            <rect x="13" y="4" width="16" height="34" rx="3" stroke="#fbbf24" strokeWidth="1.6" />
            <rect x="4" y="13" width="34" height="16" rx="3" stroke="#fbbf24" strokeWidth="1.6" />
            <polygon points="21,14 28,21 21,28 14,21" fill="rgba(252, 211, 77, 0.25)" stroke="#fde68a" strokeWidth="1.5" />
            <circle cx="21" cy="21" r="2.5" fill="#fde68a" />
          </svg>
        </div>
      );

    case 'imperial_crimson':
      return (
        <div
          style={{
            width: '100%',
            height: '100%',
            borderRadius: 5,
            background: 'radial-gradient(circle at 50% 50%, #dc2626 0%, #991b1b 60%, #450a0a 100%)',
            border: '2px solid #fbbf24',
            boxShadow: 'inset 0 0 8px rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 4,
              border: '1.5px solid rgba(253, 230, 138, 0.65)',
              borderRadius: 3,
            }}
          />
          <svg width="40" height="52" viewBox="0 0 40 52" fill="none">
            <polygon points="20,6 34,26 20,46 6,26" stroke="#fde68a" strokeWidth="1.8" fill="rgba(251,191,36,0.18)" />
            <polygon points="20,13 28,26 20,39 12,26" stroke="#fbbf24" strokeWidth="1.4" fill="#7f1d1d" />
            <path d="M14 27 L16 20 L20 24 L24 20 L26 27 Z" fill="#fde68a" />
          </svg>
        </div>
      );

    case 'cyber_neon':
      return (
        <div
          style={{
            width: '100%',
            height: '100%',
            borderRadius: 5,
            background: 'linear-gradient(135deg, #090d16 0%, #1e1b4b 50%, #090d16 100%)',
            border: '2px solid #06b6d4',
            boxShadow: 'inset 0 0 10px rgba(236, 72, 153, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 4,
              border: '1px solid #ec4899',
              borderRadius: 3,
            }}
          />
          <svg width="40" height="52" viewBox="0 0 40 52" fill="none">
            <polygon points="20,7 33,19 33,33 20,45 7,33 7,19" stroke="#06b6d4" strokeWidth="1.6" />
            <polygon points="20,14 27,26 20,38 13,26" stroke="#ec4899" strokeWidth="1.8" fill="rgba(6, 182, 212, 0.15)" />
            <circle cx="20" cy="26" r="3" fill="#22d3ee" />
          </svg>
        </div>
      );

    case 'obsidian_vip':
      return (
        <div
          style={{
            width: '100%',
            height: '100%',
            borderRadius: 5,
            background: 'linear-gradient(160deg, #1f2937 0%, #030712 60%, #111827 100%)',
            border: '2px solid #eab308',
            boxShadow: 'inset 0 0 10px rgba(234, 179, 8, 0.28)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 4,
              border: '1px solid rgba(250, 204, 21, 0.5)',
              borderRadius: 3,
            }}
          />
          <svg width="42" height="52" viewBox="0 0 42 52" fill="none">
            <circle cx="21" cy="26" r="14" stroke="#eab308" strokeWidth="1.5" strokeDasharray="3 2" />
            <circle cx="21" cy="26" r="10" stroke="#fde047" strokeWidth="1.2" />
            <text
              x="21"
              y="29"
              textAnchor="middle"
              fill="#fde047"
              fontSize="9"
              fontWeight="bold"
              fontFamily="system-ui, sans-serif"
            >
              VIP 2
            </text>
          </svg>
        </div>
      );

    case 'classic_blue':
    default:
      return (
        <div
          style={{
            width: '100%',
            height: '100%',
            background:
              'repeating-linear-gradient(45deg, #1e3a8a, #1e3a8a 8px, #2563eb 8px, #2563eb 16px)',
            borderRadius: 5,
            border: '2px solid #fff',
            boxShadow: 'inset 0 0 0 2px #1e3a8a',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              width: 24,
              height: 36,
              border: '1.5px solid rgba(255,255,255,0.75)',
              borderRadius: 4,
              background: 'rgba(30, 58, 138, 0.65)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: 14,
              fontWeight: 800,
            }}
          >
            ♠
          </div>
        </div>
      );
  }
}

export function CardUI({
  cardCode = '2S',
  selected,
  isOpeningCard,
  isPlayable,
  isDimmed,
  isTrump,
  onClick,
  onPointerDown,
  style,
  faceDown,
  cardBackId,
  className,
}: CardProps) {
  const { cardBack } = useCardBack();
  const activeBack = cardBackId ?? cardBack;

  const isRed = !faceDown && (suitOf(cardCode) === 'D' || suitOf(cardCode) === 'H');
  const color = isRed ? '#ef4444' : '#111';

  const inner = faceDown ? (
    renderCardBackDesign(activeBack)
  ) : (
    <>
      {/* Sizes are in em: .card-ui font-size is derived from --card-w so indices scale with the card. */}
      <div className="card-face-rank" style={{ position: 'absolute', top: '0.22em', left: '0.3em', color, fontSize: '1.15em', fontWeight: 'bold', lineHeight: 1, letterSpacing: '-0.06em' }}>
        {RANK_LABELS[rankOf(cardCode)]}
      </div>
      <div style={{ position: 'absolute', top: '1.35em', left: '0.32em', color, fontSize: '0.95em', lineHeight: 1 }}>
        {SUIT_SYMBOLS[suitOf(cardCode)]}
      </div>
      {isTrump && (
        <div
          className="card-trump-badge"
          title="Trump (Хөзөр)"
          aria-label="Trump card"
          style={{
            position: 'absolute',
            top: '0.22em',
            right: '0.28em',
            fontSize: '0.65em',
            color: '#f59e0b',
            lineHeight: 1,
            fontWeight: 800,
            textShadow: '0 1px 2px rgba(0,0,0,0.3)',
          }}
        >
          ★
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          bottom: '0.22em',
          right: '0.3em',
          color,
          fontSize: '1.15em',
          fontWeight: 'bold',
          lineHeight: 1,
          letterSpacing: '-0.06em',
          transform: 'rotate(180deg)',
        }}
      >
        {RANK_LABELS[rankOf(cardCode)]}
      </div>
      <div
        style={{
          position: 'absolute',
          bottom: '1.35em',
          right: '0.32em',
          color,
          fontSize: '0.95em',
          lineHeight: 1,
          transform: 'rotate(180deg)',
        }}
      >
        {SUIT_SYMBOLS[suitOf(cardCode)]}
      </div>
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color, fontSize: '2em' }}>
        {SUIT_SYMBOLS[suitOf(cardCode)]}
      </div>
    </>
  );

  return (
    <div
      data-card={faceDown ? undefined : cardCode}
      className={`card-ui${selected ? ' is-selected' : ''}${isOpeningCard ? ' is-opening-card' : ''}${isPlayable ? ' is-playable' : ''}${isDimmed ? ' is-dimmed' : ''}${isTrump ? ' is-trump-card' : ''}${faceDown ? ' is-face-down' : ''}${className ? ` ${className}` : ''}`}
      onClick={isDimmed ? undefined : onClick}
      onPointerDown={isDimmed ? undefined : onPointerDown}
      style={{
        flexShrink: 0,
        backgroundColor: faceDown ? 'transparent' : '#fff',
        border: faceDown
          ? undefined
          : isTrump
          ? '1.5px solid rgba(245, 158, 11, 0.75)'
          : '1px solid rgba(0, 0, 0, 0.28)',
        borderRadius: 6,
        boxShadow: selected
          ? '0 0 0 2.5px var(--accent-color), 0 8px 18px rgba(0,0,0,0.45)'
          : isPlayable
          ? '0 0 0 2px rgba(16, 185, 129, 0.85), 0 4px 14px rgba(16, 185, 129, 0.4)'
          : isTrump
          ? '0 0 8px rgba(245, 158, 11, 0.3), 0 2px 5px rgba(0,0,0,0.3)'
          : '0 2px 5px rgba(0,0,0,0.3)',
        position: 'relative',
        cursor: isDimmed ? 'not-allowed' : onClick ? 'pointer' : 'default',
        transform: selected ? 'translateY(var(--card-selected-lift, -14px))' : 'translateY(0)',
        touchAction: 'manipulation',
        transition: 'transform 160ms var(--ease-out, cubic-bezier(0.23, 1, 0.32, 1)), box-shadow 160ms ease, opacity 160ms ease, filter 160ms ease',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        opacity: isDimmed ? 0.45 : undefined,
        filter: isDimmed ? 'grayscale(25%)' : undefined,
        ...style,
      }}
    >
      {inner}
      {selected && !faceDown && (
        <div
          className="card-selected-tick"
          style={{
            position: 'absolute',
            top: -3,
            right: -3,
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: 'var(--accent-color)',
            color: '#000',
            fontSize: 10,
            fontWeight: 900,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.35)',
            pointerEvents: 'none',
            zIndex: 2,
          }}
          aria-label="Selected"
        >
          ✓
        </div>
      )}
    </div>
  );
}
